// Realistic mock data for Yoobikwitus forensic image verification.
// Includes high-scoring (genuine), mid-scoring (inconclusive), and low-scoring
// (likely AI-generated) reports so every UI state is reviewable.

export const NEWS_IMAGES = [
  "https://images.unsplash.com/photo-1591588211599-04eeffe9acc7?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1541872703-74c5e44368f9?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1591065411478-97722a82fe81?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1495020689067-958852a7765e?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1504711434969-e33886168f5c?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1521133573892-e44906baee46?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1518609878373-06d740f60d8b?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1494172961521-33799ddd43a5?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
];

const SOURCE_DOMAINS = [
  "reuters.com",
  "apnews.com",
  "gettyimages.com",
  "afp.com",
  "bbc.co.uk",
  "nytimes.com",
  "theguardian.com",
  "bloomberg.com",
];

const SIGNAL_NAMES = [
  "Software field",
  "Camera make and model",
  "Timestamp consistency",
  "GPS coordinates",
  "Thumbnail consistency",
  "Colour profile",
  "File format integrity",
  "XMP metadata",
];

const GENUINE_FINDINGS = {
  "Software field": "Native camera firmware detected. No editing software footprint.",
  "Camera make and model": "Canon EOS R5 metadata intact and consistent with sensor readout.",
  "Timestamp consistency": "Capture, modify and access timestamps align within milliseconds.",
  "GPS coordinates": "Geotag matches reported capture location within 12 metres.",
  "Thumbnail consistency": "Embedded thumbnail matches full-resolution image hash.",
  "Colour profile": "sRGB ICC profile preserved end-to-end without re-encoding.",
  "File format integrity": "JPEG structure clean. Original markers intact, no recompression artefacts.",
  "XMP metadata": "Photographer credit, rights, and source agency present and well-formed.",
};

const INCONCLUSIVE_FINDINGS = {
  "Software field": "Adobe Photoshop 25.x detected — common newsroom edit but not generative.",
  "Camera make and model": "Camera fields present but partial; consistent with phone capture.",
  "Timestamp consistency": "File modified 4 hours after capture. Plausible workflow gap.",
  "GPS coordinates": "GPS tag stripped. Cannot verify capture location.",
  "Thumbnail consistency": "Thumbnail differs slightly from full image — likely crop or rotation.",
  "Colour profile": "Colour profile re-mapped during export. Not necessarily suspicious.",
  "File format integrity": "Some EXIF segments removed by platform compression.",
  "XMP metadata": "Rights field empty. Source credit missing or stripped on upload.",
};

const AI_FINDINGS = {
  "Software field": "Generative AI signature detected (Stable Diffusion C2PA manifest absent).",
  "Camera make and model": "No camera metadata present — uncharacteristic for newsroom assets.",
  "Timestamp consistency": "File creation predates EXIF capture date by 9 minutes. Inconsistent.",
  "GPS coordinates": "No GPS present and image does not match known coordinates.",
  "Thumbnail consistency": "Embedded thumbnail differs structurally from full image content.",
  "Colour profile": "Synthetic colour distribution. Histogram lacks natural sensor noise floor.",
  "File format integrity": "Multiple recompression cycles detected. Quantisation tables inconsistent.",
  "XMP metadata": "XMP block contains AI-tool descriptors stripped from primary EXIF.",
};

const STATUSES = ["complete", "complete", "complete", "flagged", "processing"];

function pickFindings(score) {
  if (score >= 70) return GENUINE_FINDINGS;
  if (score >= 40) return INCONCLUSIVE_FINDINGS;
  return AI_FINDINGS;
}

function signalScoreFor(overall, idx) {
  // Spread individual signal scores around the overall score with deterministic jitter
  const jitter = [(-6), 4, -10, 8, -3, 6, -7, 2][idx % 8];
  const v = Math.max(2, Math.min(98, overall + jitter));
  return Math.round(v);
}

export function buildSignals(overall) {
  const findings = pickFindings(overall);
  return SIGNAL_NAMES.map((name, idx) => ({
    name,
    finding: findings[name],
    score: signalScoreFor(overall, idx),
  }));
}

const CASE_SEED = [
  { score: 92, status: "complete", domain: "reuters.com", title: "Protest crowd, Berlin", img: 0 },
  { score: 88, status: "complete", domain: "apnews.com", title: "Senate press briefing", img: 1 },
  { score: 76, status: "complete", domain: "afp.com", title: "Stadium opening ceremony", img: 2 },
  { score: 58, status: "flagged", domain: "instagram.com", title: "Viral street photo", img: 3 },
  { score: 47, status: "complete", domain: "twitter.com", title: "Unverified war footage still", img: 4 },
  { score: 31, status: "flagged", domain: "imgur.com", title: "Synthetic city skyline", img: 5 },
  { score: 18, status: "flagged", domain: "discord.gg", title: "AI-generated 'crowd'", img: 6 },
  { score: 81, status: "complete", domain: "gettyimages.com", title: "Awards red carpet", img: 7 },
  { score: 65, status: "complete", domain: "bbc.co.uk", title: "Wildfire perimeter, Greece", img: 0 },
  { score: 22, status: "flagged", domain: "telegram.org", title: "Fake field hospital photo", img: 1 },
  { score: 94, status: "complete", domain: "nytimes.com", title: "Capitol building exterior", img: 2 },
  { score: 71, status: "complete", domain: "bloomberg.com", title: "Trading floor close-up", img: 3 },
];

function dateOffset(daysAgo, hoursAgo = 0) {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  d.setHours(d.getHours() - hoursAgo);
  return d.toISOString();
}

export const MOCK_CASES = CASE_SEED.map((c, idx) => {
  const overall = c.score;
  return {
    id: `case_${(idx + 1).toString().padStart(4, "0")}`,
    title: c.title,
    thumbnail: NEWS_IMAGES[c.img],
    sourceDomain: c.domain,
    sourceUrl: `https://${c.domain}/article/${idx + 1}`,
    submittedUrl: `https://${c.domain}/assets/img-${idx + 1}.jpg`,
    score: overall,
    status: c.status,
    flagged: c.status === "flagged",
    submittedAt: dateOffset(Math.floor(idx / 4), (idx * 7) % 24),
    fileName: `news_${(idx + 1).toString().padStart(4, "0")}.jpg`,
    signals: buildSignals(overall),
    batchId: idx < 4 ? "batch_001" : idx < 8 ? "batch_002" : null,
  };
});

export const MOCK_BATCHES = [
  {
    id: "batch_001",
    name: "Berlin protests — morning desk",
    submittedAt: dateOffset(0, 2),
    total: 4,
    complete: 4,
    flagged: 1,
  },
  {
    id: "batch_002",
    name: "Wire feed evening review",
    submittedAt: dateOffset(1, 3),
    total: 4,
    complete: 3,
    flagged: 2,
  },
];

// Mock candidate sources returned by reverse image search.
export function buildCandidateSources(caseId) {
  const seedIndex = parseInt(caseId.split("_")[1] || "1", 10);
  return Array.from({ length: 8 }).map((_, i) => {
    const sim = Math.max(38, 99 - i * 7 - ((seedIndex * 3) % 5));
    return {
      id: `src_${caseId}_${i + 1}`,
      thumbnail: NEWS_IMAGES[(seedIndex + i) % NEWS_IMAGES.length],
      sourceDomain: SOURCE_DOMAINS[(seedIndex + i) % SOURCE_DOMAINS.length],
      url: `https://${SOURCE_DOMAINS[(seedIndex + i) % SOURCE_DOMAINS.length]}/image/${seedIndex}-${i + 1}`,
      similarity: sim,
      capturedAt: dateOffset(i + 1, i * 3),
    };
  });
}

export function dashboardStats() {
  const todayCount = MOCK_CASES.filter((c) => {
    const d = new Date(c.submittedAt);
    const now = new Date();
    return d.toDateString() === now.toDateString();
  }).length;
  const flagged = MOCK_CASES.filter((c) => c.flagged).length;
  const inProgress = 1; // one mock batch in progress for state visibility
  return {
    analysesToday: todayCount || 5,
    flaggedImages: flagged,
    batchesInProgress: inProgress,
    accuracyTrend: 0.94,
  };
}

export function scoreLabel(score) {
  if (score >= 70) return "Likely genuine";
  if (score >= 40) return "Inconclusive";
  return "Likely AI-generated";
}

export function scoreTier(score) {
  if (score >= 70) return "genuine";
  if (score >= 40) return "inconclusive";
  return "ai";
}

export const LOGO_URL =
  "https://static.prod-images.emergentagent.com/jobs/f2dae82a-9b0f-4d4e-9271-8b8e7b29247f/images/40058357c3aade0618d2881e2b138b61ad1d940c3488b532b07d84a5b698f508.png";
